/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * PERFORMANCE OPTIMIZATION OVERRIDE
 *
 * The original hook calls `queryClient.invalidateQueries()` on hydration,
 * which invalidates ALL cached queries - including product data that doesn't
 * depend on shopper context. This causes unnecessary refetches.
 *
 * This override is more selective: it only invalidates queries that actually
 * depend on shopper context (basket, customer, shopper context itself).
 * Product queries are NOT invalidated as they don't change based on shopper context.
 */

import isEqual from 'lodash/isEqual'
import {useEffect, useRef} from 'react'
import {
    useUsid,
    useShopperContext,
    useShopperContextsMutation
} from '@salesforce/commerce-sdk-react'
import {isServer, isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {useQueryClient} from '@tanstack/react-query'

// Hooks
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Constants
import {useShopperContextSearchParams} from '@salesforce/retail-react-app/app/hooks/use-shopper-context-search-params'

/*
 * This hook will set the shopper context when search params pertinant
 * to shopper context are present.
 */
export const useUpdateShopperContext = () => {
    const {site} = useMultiSite()
    const {usid} = useUsid()
    const queryClient = useQueryClient()
    const createShopperContext = useShopperContextsMutation('createShopperContext')
    const updateShopperContext = useShopperContextsMutation('updateShopperContext')
    const {data: shopperContext, isLoading} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer && Boolean(usid)}
    )
    // Handle updating the shopper context based on URL search params
    const shopperContextFromSearchParams = useShopperContextSearchParams()

    // Track if we've already invalidated on hydration to prevent multiple invalidations
    const hasInvalidatedOnHydration = useRef(false)

    /**
     * PERFORMANCE OPTIMIZATION:
     * Instead of invalidating ALL queries, only invalidate those that
     * actually depend on shopper context. Product data doesn't change
     * based on shopper context, so we skip those.
     */
    const refetchShopperContextDependentData = () => {
        // Only invalidate queries that depend on shopper context:
        // - Basket data (depends on customer session)
        // - Customer data (depends on login state)
        // - Shopper context itself
        //
        // We specifically DO NOT invalidate:
        // - Product queries (product data is the same for all shoppers)
        // - Category queries (same for all shoppers)
        // - Preferences queries (site-level, not shopper-level)

        queryClient.invalidateQueries({
            predicate: (query) => {
                const queryKey = query.queryKey
                // queryKey is typically an array like ['basket', params] or ['customer', params]
                const primaryKey = Array.isArray(queryKey) ? queryKey[0] : queryKey

                // Only invalidate shopper-dependent queries
                const shopperDependentKeys = [
                    'basket',
                    'customer',
                    'shopperContext',
                    'shopperBaskets',
                    'shopperCustomers',
                    'customerProductLists', // wishlists
                    'customerOrders'
                ]

                return shopperDependentKeys.some(key =>
                    typeof primaryKey === 'string' && primaryKey.toLowerCase().includes(key.toLowerCase())
                )
            }
        })
    }

    const handleShopperContextUpdate = async (shopperContext, newShopperContext) => {
        const payload = {
            parameters: {usid, siteId: site.id},
            body: newShopperContext
        }
        if (!shopperContext) {
            await createShopperContext.mutateAsync(payload)
        } else {
            await updateShopperContext.mutateAsync(payload)
        }
    }

    useEffect(() => {
        const shouldUpdateShopperContext =
            !isLoading &&
            Object.keys(shopperContextFromSearchParams).length > 0 &&
            !isEqual(shopperContext, shopperContextFromSearchParams)

        if (shouldUpdateShopperContext) {
            handleShopperContextUpdate(shopperContext, shopperContextFromSearchParams)
        } else if (shopperContext && isHydrated() && !hasInvalidatedOnHydration.current) {
            // Only invalidate once on hydration, and only shopper-dependent queries
            hasInvalidatedOnHydration.current = true
            refetchShopperContextDependentData()
        }
    }, [isLoading, shopperContext, shopperContextFromSearchParams])
}
